import { type FC } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { MultilevelSelectItemItem } from "~/common/components/Select/MultilevelSelect";
import CheckIcon from "@mui/icons-material/Check";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "~/common/mui";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
  register: UseFormReturn["register"];
  control: UseFormReturn["control"];
  dropdownOptions: MultilevelSelectItemItem[];
}

const RequestFeedbackPopupForm: FC<Props> = (props: Props) => {
  const { t } = useTranslation();

  const CategorySelection = ({
    currentValue,
    optionList,
  }: {
    currentValue?: string;
    optionList?: MultilevelSelectItemItem[];
  }) => {
    if (!currentValue || !optionList?.some((child) => child.value === currentValue)) {
      return null;
    }
    return (
      <Stack direction={"row"} gap={1}>
        <CheckIcon color="primary" />
        <Typography color="text.secondary">
          {optionList?.find((option) => option.value === currentValue)?.label}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Stack gap={2}>
        <Typography variant="titleMedium" component={"b"}>
          {t("serviceCases.create.subject") + "*"}
        </Typography>
        <Controller
          // titles can't be selected, so don't put them as default value, put the first selectable option
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Box>
              {props.dropdownOptions.map((option) => (
                <Accordion key={option.label}>
                  <AccordionSummary>
                    <Stack gap={0.5}>
                      <Typography variant="bodyLarge" component={"b"}>
                        {option.label}
                      </Typography>
                      <CategorySelection optionList={option.children} currentValue={value} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RadioGroup value={value} onChange={onChange} sx={{ pl: 2 }}>
                      {option.children?.map((entry) => (
                        <FormControlLabel value={entry.value} control={<Radio />} label={entry.label} />
                      ))}
                    </RadioGroup>
                  </AccordionDetails>
                </Accordion>
              ))}
              {error?.message && <Alert severity="error">{error.message}</Alert>}
            </Box>
          )}
          name={"subject"}
          control={props.control}
          rules={{ required: { value: true, message: t("contactUsPage.form.fields.subject.placeholder")! } }}
          defaultValue={null}
        />
      </Stack>
      <Stack gap={2}>
        <Typography variant="titleMedium" component={"b"}>
          {t("serviceCases.create.comment") + "*"}
        </Typography>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant={"outlined"}
              multiline
              fullWidth
              rows={2}
              label={t("request.information.comment.label")}
              helperText={t("request.information.comment.helper")}
              placeholder={t("serviceCases.create.placeholder")}
              error={!!error}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton onClick={() => field.onChange("")}>
                      <CloseIcon />
                    </IconButton>
                  ),
                },
              }}
            />
          )}
          name={"comment"}
          control={props.control}
          rules={{ required: true, maxLength: 512 }}
          defaultValue={""}
        />
      </Stack>
    </>
  );
};

export default RequestFeedbackPopupForm;
